/**
 * Slice 2E3 · Levi Vision Panel
 * =============================
 *
 * Presents the four locked backend reasoning sections that live on
 * ``session.reasoning`` alongside the seven-key ``session.recommendation``
 * contract:
 *
 *   - what_i_see               → "What I See"
 *   - possible_concerns        → "What I'd Want to Verify"
 *   - questions_before_pricing → "Questions Before Pricing"
 *   - often_forgotten          → "Don't Forget"
 *
 * This is presentation and interaction wiring only. The component:
 *   - Reads the backend keys directly (no rename, no second schema).
 *   - Defensively coerces each section to a string array so a missing
 *     ``reasoning`` object or a legacy pre-Stage-2 session renders
 *     safely without crashing.
 *   - Preserves the meaning of every section: what_i_see and
 *     possible_concerns are never combined; uncertainty wording is
 *     never altered; no confidence/confirmed/verified/detected badge
 *     is ever added.
 *   - Renders a loading state while an analysis is running, and a
 *     recoverable error state on failure that never clears the
 *     contractor's inputs.
 *
 * Backend response consumed:
 *   ``{
 *       recommendation: { ...7 locked keys... },
 *       reasoning: {
 *         what_i_see: [...],
 *         possible_concerns: [...],
 *         questions_before_pricing: [...],
 *         often_forgotten: [...],
 *       },
 *     }``
 */
import React from "react";
import { Eye, Search, HelpCircle, ClipboardCheck, Loader2, AlertCircle } from "lucide-react";
import { useLang } from "@/context/LangContext";

const REASONING_KEYS = [
  "what_i_see",
  "possible_concerns",
  "questions_before_pricing",
  "often_forgotten",
];

/** Return a clean array of trimmed strings for one reasoning key.
 *  Never throws. Never invents items. Empty / null / non-list inputs
 *  return ``[]`` so downstream renderers show the empty-state copy. */
function normalizeSection(reasoning, key) {
  const raw = reasoning && typeof reasoning === "object" ? reasoning[key] : null;
  if (!Array.isArray(raw)) return [];
  const out = [];
  for (const item of raw) {
    if (item === null || item === undefined) continue;
    const s = String(item).trim();
    if (s) out.push(s);
  }
  return out;
}

/** One reasoning section — heading + supporting copy + list or empty state. */
function ReasoningSection({ testid, Icon, heading, help, items, emptyCopy }) {
  const hasItems = items.length > 0;
  return (
    <section
      data-testid={testid}
      data-reasoning-key={testid.replace("levi-vision-", "")}
      className="rounded-lg border border-[var(--jc-border)] bg-white p-4 sm:p-5"
    >
      <div className="flex items-start gap-2.5">
        <Icon
          size={16}
          className="mt-0.5 shrink-0 text-[var(--jc-blue)]"
          aria-hidden="true"
        />
        <div className="flex-1 min-w-0">
          <h3
            className="font-display text-base sm:text-lg text-[var(--jc-navy)] leading-tight"
            data-testid={`${testid}-heading`}
          >
            {heading}
          </h3>
          <p
            className="text-xs sm:text-sm text-[var(--jc-text-soft)] mt-1"
            data-testid={`${testid}-help`}
          >
            {help}
          </p>
        </div>
      </div>
      {hasItems ? (
        <ul
          className="mt-3 pl-1 sm:pl-2 space-y-2 text-sm text-[var(--jc-navy)] leading-snug"
          data-testid={`${testid}-list`}
        >
          {items.map((item, i) => (
            <li
              key={i}
              data-testid={`${testid}-item-${i}`}
              className="flex gap-2 break-words"
            >
              <span
                aria-hidden="true"
                className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--jc-blue)]/60"
              />
              <span className="min-w-0">{item}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p
          data-testid={`${testid}-empty`}
          className="mt-3 text-sm italic text-[var(--jc-text-soft)]"
        >
          {emptyCopy}
        </p>
      )}
    </section>
  );
}

/**
 * Public component.
 *
 * Props:
 *   - reasoning    : object with the four locked keys (may be null / missing).
 *   - loading      : boolean · true while an analyze request is in flight.
 *   - error        : boolean · true when the last analyze failed and no
 *                    recommendation is available yet. When true, existing
 *                    inputs (photos, description, voice notes) are still
 *                    intact upstream — this panel does NOT clear them.
 *
 * The component never renames backend keys, never fabricates items,
 * never introduces confidence scores or confirmed-damage badges, and
 * always visually separates ``what_i_see`` from ``possible_concerns``.
 */
export const LeviVisionPanel = ({ reasoning, loading = false, error = false }) => {
  const { t } = useLang();

  if (loading) {
    return (
      <section
        className="jc-card p-6 space-y-3"
        data-testid="levi-vision-panel-loading"
        role="status"
        aria-live="polite"
      >
        <div className="flex items-center gap-2 text-[var(--jc-navy)]">
          <Loader2 className="animate-spin text-[var(--jc-blue)]" size={16} aria-hidden="true" />
          <h2 className="font-display text-lg" data-testid="levi-vision-panel-loading-title">
            {t("levi_estimate.vision_panel_loading_title")}
          </h2>
        </div>
        <p className="text-sm text-[var(--jc-text-soft)]" data-testid="levi-vision-panel-loading-body">
          {t("levi_estimate.vision_panel_loading_body")}
        </p>
      </section>
    );
  }

  if (error) {
    return (
      <section
        className="jc-card p-6 space-y-3 border border-amber-200 bg-amber-50/60"
        data-testid="levi-vision-panel-error"
        role="alert"
      >
        <div className="flex items-start gap-2 text-amber-900">
          <AlertCircle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
          <h2 className="font-display text-lg" data-testid="levi-vision-panel-error-title">
            {t("levi_estimate.vision_panel_error_title")}
          </h2>
        </div>
        <p className="text-sm text-amber-900/90" data-testid="levi-vision-panel-error-body">
          {t("levi_estimate.vision_panel_error_body")}
        </p>
      </section>
    );
  }

  // Defensive normalization of the four sections. Legacy sessions
  // without a stored ``reasoning`` object surface here as four empty
  // arrays and the section-level empty states render.
  const [whatISee, possibleConcerns, questions, forgotten] =
    REASONING_KEYS.map((k) => normalizeSection(reasoning, k));

  return (
    <section
      className="jc-card p-4 sm:p-6 space-y-4"
      data-testid="levi-vision-panel"
      aria-labelledby="levi-vision-panel-title"
    >
      <header className="space-y-1">
        <h2
          id="levi-vision-panel-title"
          className="font-display text-lg sm:text-xl text-[var(--jc-navy)]"
          data-testid="levi-vision-panel-title"
        >
          {t("levi_estimate.vision_panel_title")}
        </h2>
        <p
          className="text-xs sm:text-sm text-[var(--jc-text-soft)]"
          data-testid="levi-vision-panel-intro"
        >
          {t("levi_estimate.vision_panel_intro")}
        </p>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:gap-4">
        <ReasoningSection
          testid="levi-vision-what-i-see"
          Icon={Eye}
          heading={t("levi_estimate.vision_panel_what_i_see_heading")}
          help={t("levi_estimate.vision_panel_what_i_see_help")}
          items={whatISee}
          emptyCopy={t("levi_estimate.vision_panel_what_i_see_empty")}
        />
        <ReasoningSection
          testid="levi-vision-possible-concerns"
          Icon={Search}
          heading={t("levi_estimate.vision_panel_possible_concerns_heading")}
          help={t("levi_estimate.vision_panel_possible_concerns_help")}
          items={possibleConcerns}
          emptyCopy={t("levi_estimate.vision_panel_possible_concerns_empty")}
        />
        <ReasoningSection
          testid="levi-vision-questions"
          Icon={HelpCircle}
          heading={t("levi_estimate.vision_panel_questions_heading")}
          help={t("levi_estimate.vision_panel_questions_help")}
          items={questions}
          emptyCopy={t("levi_estimate.vision_panel_questions_empty")}
        />
        <ReasoningSection
          testid="levi-vision-forgotten"
          Icon={ClipboardCheck}
          heading={t("levi_estimate.vision_panel_forgotten_heading")}
          help={t("levi_estimate.vision_panel_forgotten_help")}
          items={forgotten}
          emptyCopy={t("levi_estimate.vision_panel_forgotten_empty")}
        />
      </div>
    </section>
  );
};

export default LeviVisionPanel;
